import {Component, OnInit} from '@angular/core';
import {Handler} from "src/app/common/handler";
import {ConnectionType, DataType} from "@flowmaps-com/flowmaps-typescriptmodels";
import {BaseMeasurementChartComponent} from "../base-measurement-chart";
import {ChartCompare, ChartOptionType, GasConsumptionView} from "../../dashboard/dashboard.types";
import {scatterExclusiveTooltipPlugin, syncScatterYAxisPlugin} from "../bar-chart/bar-chart.component";
import {AppContext} from "../../../app-context";
import {DashboardContext} from "../../dashboard/dashboard.context";
import {MeasurementDataset} from "../base-measurement-chart";
import {cloneDeep} from "lodash";
import {TranslateDirective} from "../../../common/utils/translate.directive";
import {
  climateNormalizationFactor,
  completedClimateNormalizationRange,
  colorWithOpacity,
  getClimateNormalizationTotals,
  supportsClimateNormalizationResolution
} from './gas-climate-normalization';
import {combineLatest} from 'rxjs';
import {DataQueryFilters} from '../../measurements-component/measurements-handler.component';
import {Measurements} from '../../measurements-component/measurements';
import moment from 'moment';

@Component({
  selector: 'app-gas-chart',
  templateUrl: './gas-chart.component.html',
  styleUrls: ['./gas-chart.component.scss']
})
@Handler()
export class GasChartComponent extends BaseMeasurementChartComponent implements OnInit {
  chartOptionType: ChartOptionType = ChartOptionType.Gas;
  mainConsumptionType: DataType = DataType.gasConsumption;
  mainConnectionType: ConnectionType = ConnectionType.Gas;
  connectionTypes: ConnectionType[] = [ConnectionType.Gas, ConnectionType.Heat];
  contractedCapacityRequiredForPower: boolean = true;
  private climateDataBeforeRefresh: typeof this._rawData;
  readonly climateComparisonTableLabels = {
    previous: TranslateDirective.getTranslation('Actual consumption', true),
    current: TranslateDirective.getTranslation('Climate normalized', true),
    difference: TranslateDirective.getTranslation('Difference', true)
  };
  measurementUnit = () => this.showingCooling
    ? DashboardContext.getMeasurementUnit(DataType.coolingConsumption)
    : this.showConsumptionPerDegreeDay
      ? DashboardContext.getMeasurementUnit(DataType.gasConsumptionPerHdd)
      : DashboardContext.getMeasurementUnit(this.mainConsumptionType);

  override ngOnInit(): void {
    super.ngOnInit();
    combineLatest([
      this.subscribeTo("getChartOptions", this.chartOptionType),
      this.subscribeTo("getChartDataQueryFilters")
    ]).subscribe(([options, filters]) => {
      if (!supportsClimateNormalizationResolution(filters?.timeRange?.resolution)
        && options?.gasConsumptionView !== 'actual') {
        options.gasConsumptionView = 'actual';
      }
    });
  }

  openModal() {
    super.openModal(GasChartComponent);
  }

  get showingCooling(): boolean {
    return this.options?.showCooling ?? this.options?.thermalConsumptionView === 'cooling';
  }

  get coolingOnly(): boolean {
    const types = (this.filters?.allSelectedSources || []).map(source => source.connectionType);
    return types.includes(ConnectionType.Cooling)
      && !types.some(type => type === ConnectionType.Gas || type === ConnectionType.Heat);
  }

  get chartTitle(): string {
    if (!this.showingCooling) return 'Gas and heat consumption';
    if (this.coolingOnly) return 'Cooling consumption';
    return (this.filters?.allSelectedSources || []).some(source => source.connectionType === ConnectionType.Gas)
      ? 'Gas, heat and cooling consumption' : 'Heat and cooling consumption';
  }

  setShowCooling(show: boolean): void {
    this.options.showCooling = show;
    if (show) {
      this.options.showConsumptionPerDegreeDay = false;
      this.showConsumptionPerDegreeDay = false;
    }
    this.refreshAfterFilterChange(this.filters);
  }

  protected override refreshAfterFilterChange(filters: DataQueryFilters): void {
    // An actual-only response is not evidence of missing climate data while the new result is loading.
    this.climateDataBeforeRefresh = this.gasConsumptionView === 'actual' ? undefined : this._rawData;
    const types = (filters?.allSelectedSources || []).map(source => source.connectionType);
    if (this.options.showCooling === undefined && !this.options.thermalConsumptionView
        && types.includes(ConnectionType.Cooling)
        && !types.some(type => type === ConnectionType.Gas || type === ConnectionType.Heat)) {
      this.options.showCooling = true;
    }
    this.mainConsumptionType = this.showingCooling ? DataType.heatConsumption : DataType.gasConsumption;
    this.mainConnectionType = this.showingCooling
      ? (types.includes(ConnectionType.Gas) ? ConnectionType.Gas
        : types.includes(ConnectionType.Heat) ? ConnectionType.Heat : ConnectionType.Cooling)
      : ConnectionType.Gas;
    this.connectionTypes = this.showingCooling
      ? [ConnectionType.Gas, ConnectionType.Heat, ConnectionType.Cooling] : [ConnectionType.Gas, ConnectionType.Heat];
    super.refreshAfterFilterChange(filters);
  }

  measurementTypes = (): DataType[] => {
    if (this.showingCooling) return [DataType.gasConsumption, DataType.heatConsumption, DataType.coolingConsumption];
    if (this.showConsumptionPerDegreeDay) {
      return [DataType.gasConsumptionPerHdd, DataType.heatConsumptionPerHdd];
    }
    return [DataType.gasConsumption, DataType.heatConsumption];
  };

  protected override usesDashboardTotals(): boolean {
    // The complete dashboard stream is already subscribed and includes heat and cooling.
    // Keep one source of chart data instead of also opening a parallel totals stream.
    return false;
  }

  protected override measurementDisplayFactor(type: DataType): number {
    if (!this.showingCooling) return super.measurementDisplayFactor(type);
    // Adjacent heating/cooling bars use one energy scale; stored values retain their source units.
    return type === DataType.gasConsumption ? 1 / DashboardContext.gjToM3Rate : 1;
  }

  protected override filterMeasurementDatasetsForDisplay(datasets: MeasurementDataset[]): MeasurementDataset[] {
    if (!this.showingCooling) return datasets;
    return datasets.map(series => {
      if (![DataType.gasConsumption, DataType.heatConsumption, DataType.coolingConsumption].includes(series.measurementType)) {
        return series;
      }
      const period = series.dataset.stack;
      return {...series, displayUnit: 'GJ', comparisonStack: period, dataset: {
        ...series.dataset,
        order: series.measurementType === DataType.coolingConsumption
          ? AppContext.indexOfDataType(DataType.heatConsumption) + 1 + Number(series.dataset.order || 0)
          : series.dataset.order,
        stack: `${period} - ${series.measurementType === DataType.coolingConsumption ? 'cooling' : 'heating'}`,
        tooltip: {...series.dataset.tooltip, formatter: this.chartUtils.getCustomTooltipFormatter(DataType.coolingConsumption)}
      }};
    });
  }

  measurementIntermediateLink = (): Map<DataType, DataType[]> => new Map<DataType, DataType[]>();

  consumptionProductionLink = (): Map<DataType, DataType> => new Map<DataType, DataType>();

  measurementTypesMapped = (): {[key: string]: DataType[]} => this.showingCooling
    ? {
      [DataType.gasConsumption]: [DataType.gasConsumption],
      [DataType.heatConsumption]: [DataType.heatConsumption],
      [DataType.coolingConsumption]: [DataType.coolingConsumption]
    } : {};

  productionDataTypesMapped = (): {[key: string]: DataType[]} => {
    const measurementTypes = this.productionDataTypes();
    return {
      [measurementTypes[0]]: measurementTypes
    };
  }

  showConsumptionPerDegreeDayChange(showConsumptionPerDegreeDay: boolean) {
    this.options.showConsumptionPerDegreeDay = showConsumptionPerDegreeDay;
    this.refreshAfterFilterChange(this.filters);
  }

  get gasConsumptionView(): GasConsumptionView {
    return this.options?.gasConsumptionView || 'actual';
  }

  setGasConsumptionView(view: GasConsumptionView): void {
    if (view !== 'actual' && !this.climateNormalizationCanBeRequested) return;
    if (view === 'compareClimate' && this.options.compare.enabled) {
      super.compareChange(false);
    }
    this.options.gasConsumptionView = view;
    this.refreshAfterFilterChange(this.filters);
  }

  setClimateYearCorrected(enabled: boolean): void {
    this.setGasConsumptionView(enabled ? 'climateNormalized' : 'actual');
  }

  setClimateComparison(enabled: boolean): void {
    this.setGasConsumptionView(enabled ? 'compareClimate' : 'climateNormalized');
  }

  override compareChange(compare: boolean): void {
    if (compare && this.gasConsumptionView === 'compareClimate') {
      this.options.gasConsumptionView = 'climateNormalized';
      this.refreshData(true);
    }
    super.compareChange(compare);
  }

  override comparedYearChange(compareInfo: ChartCompare): void {
    if (this.gasConsumptionView === 'compareClimate') {
      this.options.gasConsumptionView = 'climateNormalized';
      this.refreshData(true);
    }
    super.comparedYearChange(compareInfo);
  }

  get climateNormalizationAvailable(): boolean {
    return this.climateNormalizationCanBeRequested && !!this.currentClimateNormalization;
  }

  get climateNormalizationCanBeRequested(): boolean {
    return !this.coolingOnly && supportsClimateNormalizationResolution(this.timeRange?.resolution)
      && !this.isFullElectricSelection;
  }

  get climateNormalizationUnavailableMessage(): string {
    if (this.coolingOnly) return TranslateDirective.getTranslation('Heating degree-day correction is not available for cooling consumption.', true);
    if (!supportsClimateNormalizationResolution(this.timeRange?.resolution)) {
      return TranslateDirective.getTranslation(
        'Not available. Climate year normalization is available in month and year views.', true);
    }
    if (this.isFullElectricSelection) {
      return TranslateDirective.getTranslation(
        'Not available. Climate normalization is not possible for a Full Electric building.', true);
    }
    if (!completedClimateNormalizationRange(this.timeRange, this.timeRange?.resolution)) {
      return TranslateDirective.getTranslation(
        'Climate normalization is applied to completed months and years. The current period still shows actual consumption.', true);
    }
    if (!this._rawData || this.climateDataBeforeRefresh === this._rawData
      || this.climateNormalizationAvailable) return null;
    return TranslateDirective.getTranslation(
      'Not available. Complete climate data and at least one heating degree day are required for the selected period.', true);
  }

  protected override transformAggregatedMeasurementDatasets(datasets: MeasurementDataset[], isPrevious: boolean,
                                                   isEstimated: boolean): MeasurementDataset[] {
    const view = this.gasConsumptionView;
    if (view === 'actual' || !this.climateNormalizationCanBeRequested) return datasets;
    const currentContext = this.climateNormalizationContext(false);
    if (!currentContext.totals) return datasets;
    const context = isPrevious ? this.climateNormalizationContext(true) : currentContext;
    if (!context.totals) return datasets;
    const slots = Measurements.getSlots(isPrevious ? context.range : this.displayTimeRange);
    const start = moment(context.completedRange.start).valueOf();
    const end = moment(context.completedRange.end).valueOf();
    const completedSlots = slots.map(slot => slot.startTime >= start && slot.endTime <= end);
    const factor = climateNormalizationFactor(context.totals);
    const normalized = datasets.map(dataset => this.normalizeDataset(dataset, factor, slots, completedSlots));
    if (view === 'climateNormalized') return normalized;
    // Cooling is unchanged by climate correction, so both comparison columns retain its actual value.
    const comparedEnergy = (dataset: MeasurementDataset) => this.isClimateSensitiveDataset(dataset)
      || (this.showingCooling && dataset.measurementType === DataType.coolingConsumption);
    const actualComparison = datasets.map(dataset => comparedEnergy(dataset)
      ? this.asActualComparisonDataset(dataset) : dataset);
    const normalizedEnergy = normalized.filter(comparedEnergy);
    return actualComparison.concat(normalizedEnergy);
  }

  private normalizeDataset(dataset: MeasurementDataset, factor: number,
                           slots: ReturnType<typeof Measurements.getSlots>, completedSlots: boolean[]): MeasurementDataset {
    if (!this.isClimateSensitiveDataset(dataset)) return dataset;
    const normalized = cloneDeep(dataset);
    normalized.dataset.data = normalized.dataset.data.map((value, index) =>
      completedSlots[index] && typeof value === 'number' ? value * factor : value);
    const hasProvisionalValues = dataset.dataset.data.some((value, index) =>
      !completedSlots[index] && typeof value === 'number');
    const label = hasProvisionalValues
      ? 'Climate normalized (provisional periods show actual consumption)' : 'Climate normalized';
    normalized.dataset.label = `${dataset.dataset.label} - ${TranslateDirective.getTranslation(label, true)}`;
    if (hasProvisionalValues) {
      normalized.dataset.tooltipLabels = slots.map((slot, index) => completedSlots[index] ? slot.label
        : `${slot.label} - ${TranslateDirective.getTranslation('Actual consumption (provisional period)', true)}`);
    }
    normalized.dataset.borderWidth = 2;
    normalized.dataset.borderDash = [6, 4];
    normalized.dataset.stack = dataset.dataset.stack;
    return normalized;
  }

  private asActualComparisonDataset(dataset: MeasurementDataset): MeasurementDataset {
    const comparison = cloneDeep(dataset);
    const color = comparison.dataset.borderColor || comparison.dataset.backgroundColor;
    comparison.dataset.backgroundColor = colorWithOpacity(color, 0.35) as typeof comparison.dataset.backgroundColor;
    comparison.dataset.borderColor = color;
    comparison.dataset.borderWidth = 1;
    comparison.dataset.label = `${dataset.dataset.label} - ${TranslateDirective.getTranslation('Actual consumption', true)}`;
    comparison.dataset.stack = DashboardContext.stacks.lastYear;
    return comparison;
  }

  private isClimateSensitiveDataset(dataset: MeasurementDataset): boolean {
    return dataset.measurementType === DataType.gasConsumption || dataset.measurementType === DataType.heatConsumption;
  }

  private get currentClimateNormalization() {
    return this.climateNormalizationContext(false).totals;
  }

  private climateNormalizationContext(isPrevious: boolean) {
    if (this.climateDataBeforeRefresh !== this._rawData) this.climateDataBeforeRefresh = undefined;
    const data = isPrevious ? this._rawPreviousData : this._rawData;
    const range = isPrevious ? this.comparedTimeRange : this.timeRange;
    const completedRange = completedClimateNormalizationRange(range, range?.resolution);
    const points = (data?.totals || []).flatMap(result =>
      result.measurements?.[DataType.climateNormalizationDegreeDays] || []);
    return {range, completedRange, totals: getClimateNormalizationTotals(completedRange, points)};
  }

  private get isFullElectricSelection(): boolean {
    return (this.filters?.allSelectedSources || []).some(source => source.source?.location?.info?.allElectric === true);
  }

  plugins = [scatterExclusiveTooltipPlugin, syncScatterYAxisPlugin];
  protected readonly AppContext = AppContext;
}
